import { find, intersection, isNil } from 'lodash';

import { Setpoint } from '@/store/Engine/common';
import DevicesStore from '@/store/Devices/DevicesStore';
import { EngineStore } from '@/store/Engine/EngineStore';
import { t } from 'i18next';

import { DeviceType } from '@/constants/deviceTypeEnum';

export const LONG_DASH = '\u2014';

export const bandConfigurationOptions = [LONG_DASH, '1', '2'];

export type DefaultSetpointsFrom = 'rulesEditor' | 'rulesGeneratorModal';

const getEditableStatus = ({
  engineStore,
  devicesStore,
  field,
  value,
  compressorId,
  setpointKey,
}: {
  engineStore: EngineStore;
  devicesStore: DevicesStore;
  field: string;
  value: string | boolean | number;
  compressorId: string;
  setpointKey: string;
}) => {
  const editableFlag = !!intersection(
    find(devicesStore.compressors, { id: compressorId })?.editableFields,
    columnsMapping[field],
  )?.length;
  const oldValue = engineStore.setpointsConfiguration?.getOldSetpointByKey(setpointKey)?.[field];
  const hasBeenEdited = isNil(oldValue) ? !isNil(value) : oldValue !== value;

  return { editableFlag, hasBeenEdited };
};

const isInvalidFieldValue = ({ fieldName, fieldValue }: { fieldName: string; fieldValue: string | boolean }) => {
  if (fieldName === 'modulate') {
    return false;
  }

  switch (fieldValue) {
    case true:
    case false:
      return false;
    case '':
    case null:
    case '—':
    case undefined:
      return true;
    default:
      return false;
  }
};

export const shouldDisableDraftPublishing = (
  engineStore: EngineStore,
  devicesStore: DevicesStore,
): {
  field: string;
  disabled: boolean;
  table: string;
  compressor: string;
} => {
  const draftSetpoints: Setpoint[] = engineStore.setpointsConfiguration.setpoints;
  const fields: string[] = Object.keys(columnsMapping);

  for (const setpoint of draftSetpoints) {
    for (const field of fields) {
      const { editableFlag } = getEditableStatus({
        engineStore,
        devicesStore,
        field,
        value: setpoint[field],
        compressorId: setpoint.compressorId,
        setpointKey: setpoint.key,
      });

      if (editableFlag && isInvalidFieldValue({ fieldName: field, fieldValue: setpoint[field] })) {
        return {
          field,
          table: setpoint.name,
          compressor: setpoint.compressorNumber,
          disabled: true,
        };
      }
    }
  }

  return {
    field: null,
    table: null,
    compressor: null,
    disabled: false,
  };
};

export const rulesKeys = [
  { key: 'order', titleKey: 'ord' },
  { key: 'load', titleKey: 'lo' },
  { key: 'vsdTarget', titleKey: 'target' },
  { key: 'unload', titleKey: 'un' },
  { key: 'autoStop', titleKey: 'aut' },
  { key: 'immediateStop', titleKey: 'imm' },
  { key: 'pressureDrop', titleKey: 'pd' },
];

export const columnsMapping = {
  order: ['order'],
  load: ['load'],
  unload: ['unload'],
  vsdTarget: ['target'],
  autoStop: ['auto'],
  immediateStop: ['immediate'],
  modulate: ['modulation'],
  bandConfiguration: ['band'],
  pressureDrop: ['pressureDrop'],
};

export const negativeColumns = ['order'];

export const compressorTypesAbbreviations = {
  [DeviceType.FixedSpeed]: 'FS',
  [DeviceType.VariableSpeedDrive]: 'VSD',
  [DeviceType.Centrifugal]: 'C',
  [DeviceType.CentrifugalAndPowerSensor]: 'CPS',
  [DeviceType.FixedSpeedAndPowerSensor]: 'FSPS',
  [DeviceType.VariableSpeedDriveAndPowerSensor]: 'VSDPS',
};

export const getFieldEditableStatusForAllCompressors = (compressors, compressorId, field) => {
  const compressor = find(compressors, { id: compressorId });
  const editableFields = compressor?.editableFields;

  return !!intersection(editableFields, columnsMapping[field])?.length;
};

export const shouldUpdateField = (recordName: string, prevValues: string, curValues: string) => {
  return prevValues[recordName] !== curValues[recordName];
};

export enum ValidMetricTypes {
  MinimumPressureBand1Bar = 'minimumPressureBand1Bar',
  MinimumPressureBand2Bar = 'minimumPressureBand2Bar',
  MaximumPressureBand1Bar = 'maximumPressureBand1Bar',
  MaximumPressureBand2Bar = 'maximumPressureBand2Bar',
}

export const metricNamingMap = {
  minimumPressureBand1Bar: t('loadBand1'),
  maximumPressureBand1Bar: t('unloadBand1'),
  minimumPressureBand2Bar: t('loadBand2'),
  maximumPressureBand2Bar: t('unloadBand2'),
};
